import { useGetEnrollmentMultiWelcomeData, useSubmitEnrollmentMultiWelcomeData } from '@egw/api'
import { useAuth } from '@egw/auth'
import { Button, GenericError, ProgressBar } from '@egw/components'
import { type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { sendEvent, setContext } from '@egw/events'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { CurrencyDollarIcon, KeyIcon, ChartPieIcon } from '@heroicons/react/24/outline'
import { useEffect, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { WelcomeStepV2 } from '../../../components/welcome-step-v2/welcome-step-v2'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'

const log = new Logger('Enrollment - Multi - Welcome')

export const WelcomePage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const { flags } = useFlags<FeatureFlags>()
  const { refreshToken } = useAuth()

  // Refresh token on mount as user should theoretically be authenticated
  // This will ensure that if the user skips funding, they will reach Overview page
  useEffect(() => {
    void refreshToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data, isLoading, error } = useGetEnrollmentMultiWelcomeData()
  const { mutate, isLoading: isSubmitting } = useSubmitEnrollmentMultiWelcomeData()

  const firstName = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.firstName : ''
  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''
  const userId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.userId : ''
  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.progress : undefined

  // Protect route
  useValidateRoute(data?.path)

  // After initial data is loaded, set userId through context update for Braze
  useEffect(() => {
    if (!isLoading && userId) {
      setContext({ userId })
    }
  }, [userId, isLoading])

  const onSubmit = async (): Promise<void> => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_WELCOME_CONTINUE_CLICKED, {
      applicationId,
    })
    try {
      // Otherwise, use backend-driven navigation
      const response = await mutate({
        proceedWelcome: true,
      })

      // If path exists and is different
      if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_WELCOME_SUBMIT, { applicationId })
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.CREATE_PASSKEY.path}`)
      } else if (!response?.path) {
        // If no path from BE, redirect to error
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const step1Title = 'multi.welcome.fund_personal_portfolio_yeet_coastal'

  const step1Description = flags.rolloutJointEnrollmentV1
    ? 'multi.welcome.fund_personal_portfolio_description_joint_enrollment'
    : 'multi.welcome.fund_personal_portfolio_description'

  return (
    <EnrollmentMultiLayout title={t('multi.welcome.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <>
          <div>
            <ProgressBar showAnimation progressPercentage={progress} className="mb-2xl" />
            <div className="flex flex-col sm:flex-row mb-lg sm:gap-xs gap-2xs">
              <h1 className="typo-primary-4xl-light text-text-header">{t('multi.welcome.greeting')}</h1>
              <h1 className="typo-primary-4xl-semibold text-text-header">{firstName},</h1>
            </div>
            <p className="mb-xl typo-primary-xl-regular text-text-body">{t('multi.welcome.just_starting')}</p>
            <div className="flex flex-col">
              <h3 className="typo-primary-lg-light text-text-header mb-md">{t('multi.welcome.whats_next')}</h3>
              <WelcomeStepV2
                icon={<KeyIcon className="size-6" />}
                testId="earn-with-savings"
                title={t('multi.welcome.set_passkey')}
                description={t('multi.welcome.set_passkey_description')}
              />
              <WelcomeStepV2
                icon={<CurrencyDollarIcon className="size-6" />}
                testId="fund-checking"
                title={t(step1Title)}
                description={t(step1Description)}
              />
              <WelcomeStepV2
                icon={<ChartPieIcon className="size-6" />}
                testId="optimize-investments"
                title={t('multi.welcome.optimize_investments')}
                description={t('multi.welcome.optimize_investments_description')}
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full md:w-min mt-xl"
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            testId="primaryAction_button"
            onClick={() => {
              void onSubmit()
            }}
          >
            {t('common.continue')}
          </Button>
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
